/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { MSALService } from '@content-platform/web-angular-shared-util-auth';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';
import { flatten } from 'lodash';
import { EMPTY, catchError, forkJoin, from, map, of, pluck, tap, throwError } from 'rxjs';
import { MsGraphApiHelper } from '../helper/ms-graph-api.helper';
import { API_VERSION, GRAPH_URL, RESOURCE_NOTFOUND_ERRCODE } from '../ms-graph-api.config';
import { Configuration } from '@content-platform/web-angular-shared-util-configuration';
import * as i0 from "@angular/core";
import * as i1 from "@content-platform/web-angular-shared-util-logging";
import * as i2 from "@content-platform/web-angular-shared-util-auth";
import * as i3 from "../helper/ms-graph-api.helper";
const UUID_REGEX = new RegExp('[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}');
const cachedUsers = {};
export class MsGraphApiService {
    constructor(loggerService, msalService, msGraphApiHelper) {
        this.loggerService = loggerService;
        this.msalService = msalService;
        this.msGraphApiHelper = msGraphApiHelper;
        this.logger = this.loggerService.instance('MsGraphApiService');
    }
    /**
     * Sends an invitation to a user, thereby creating it in Active Directory,
     * then adds the generated user to the Guest security group for the Content Platform.
     * @param invitationRequest Options for sending the invitation to a user. Requires invitedUserEmailAddress and inviteRedirectUrl.
     * @returns The response from the invitation request, including the UUID and invitation URL.
     */
    initializeExternalUser(invitationRequest) {
        return this.inviteUser(invitationRequest).pipe(tap((response) => {
            this.addUserToSecurityGroup(Configuration.get('azure.ad.group-external'), response.invitedUser.id);
        }));
    }
    inviteUser(invitationRequest) {
        const graphInvitation = this.msalService.graphClient
            .api('/invitations')
            .post(invitationRequest);
        return from(graphInvitation);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addUserToSecurityGroup(groupUuid, userUuid) {
        const graphSecurityGroup = this.msalService.graphClient
            .api(`/groups/${groupUuid}/members/$ref`)
            .post({
            // Links the user in Active Directory to the group
            '@odata.id': GRAPH_URL + API_VERSION + '/users/' + userUuid
        });
        return from(graphSecurityGroup);
    }
    /**
     * Checks if a user is the owner of the specified group
     * @param groupUuid group id
     * @param userUuid user object id
     * @returns boolean value for group ownership
     */
    isGroupOwner(groupUuid, userUuid) {
        const groupOwners = this.msalService.graphClient.api(`/groups/${groupUuid}/owners`).get();
        return from(groupOwners).pipe(pluck('value'), map((owners) => owners.find((x) => x.id === userUuid)), map((user) => !!user), catchError((err) => throwError(err)));
    }
    /**
     * Checks if a user is present in a specific group
     * @param userUuid user object id
     * @param groupUuid group id
     * @returns boolean value for user presence
     */
    checkIfUserExistsInGroup(userUuid, groupUuid) {
        const userGroupsById = this.msalService.graphClient
            .api(`/users/${userUuid}/memberOf?$filter=id eq '${groupUuid}'`)
            .get();
        return from(userGroupsById).pipe(map((result) => result.value.length > 0), 
        // RESOURCE_NOTFOUND_ERRCODE is returned when the user is not part of specified group
        catchError((error) => error.code === RESOURCE_NOTFOUND_ERRCODE ? of(false) : throwError(error)));
    }
    /**
     * Uses the getUsers function to retrieve a single user profile
     *
     * @param id user object id
     * @returns UserProfile
     */
    getUser(id) {
        const userById = this.msalService.graphClient
            .api(`${'/users/'}${id}?${this.getMsGraphApiUserProperties()}`)
            .get();
        return from(userById).pipe(map((resUsers) => this.msGraphApiHelper.mapUserProfileToOldGraphApi(resUsers)));
    }
    getGraphUser(id) {
        const userById = this.msalService.graphClient
            .api(`${'/users/'}${id}?${this.getMsGraphApiUserProperties()}`)
            .get();
        return from(userById).pipe(map((resUsers) => resUsers));
    }
    /**
     * Retrieves all the user data from the passed in user id.
     * The function also caches users, to minimize the amount of graph requests.
     *
     * @param ids user object id
     * @returns UserProfile list
     */
    getUsers(ids) {
        ids = ids.filter((id) => UUID_REGEX.test(id));
        if (ids.length === 0) {
            this.logger.error('Invalid user ID');
            return EMPTY;
        }
        const nonCachedUserIds = ids.filter((id) => !this.getCacheUsers(id));
        if (nonCachedUserIds.length > 0) {
            return this.retrieveUserProfilesLegacyFormat(ids).pipe(tap((users) => this.cacheUsers(users)), map(() => ids.map((id) => this.getCacheUsers(id))), catchError((err) => of(err)));
        }
        return of(ids.map((id) => this.getCacheUsers(id)));
    }
    /**
     * Retrieves all the user data from the passed in user ldap.
     * which is the mailNickname in the graph url.
     *
     * @param ldaps user ldap list
     * @returns UserProfile list
     */
    getUsersByLdap(ldaps) {
        if (!ldaps || ldaps.length === 0) {
            this.logger.error('Invalid user ID');
            return of([]);
        }
        let i, j;
        const chunk = 10;
        const requestChunks = [];
        for (i = 0, j = ldaps.length; i < j; i += chunk) {
            const temparray = ldaps.slice(i, i + chunk);
            requestChunks.push(this.getUsersByMailNickName(temparray));
        }
        return forkJoin(requestChunks).pipe(map((response) => flatten(response)));
    }
    getCacheUsers(userId) {
        return cachedUsers[userId];
    }
    /**
     * Caches users in the cachedUsers object by the user objectId
     *
     * @param users UserProfile list
     */
    cacheUsers(users) {
        users.forEach((user) => (cachedUsers[user.objectId] = user));
    }
    // FIXME Move calls to map new UserProfileMsGraph to UserProfile to the callers in the codebase that need it, instead of doing it here.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    retrieveUserProfilesLegacyFormat(userUuids) {
        const userList = this.fetchUserProfilesFromGraphApi(userUuids);
        return userList.pipe(map((resUsers) => {
            let tempUsers = [];
            resUsers.forEach((user) => {
                tempUsers = tempUsers.concat(user.value);
            });
            const responsePayLoad = tempUsers.map((element) => this.msGraphApiHelper.mapUserProfileToOldGraphApi(element));
            return responsePayLoad;
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fetchUserProfilesFromGraphApi(userUuids) {
        const MAX_IDS = 1000;
        const requests = [];
        for (let i = 0; i < userUuids.length; i += MAX_IDS) {
            const directoryObject = {
                ids: userUuids.slice(i, i + MAX_IDS),
                types: ['user']
            };
            requests.push(this.msalService.graphClient
                .api(`${'/directoryObjects/getByIds?'}${this.getMsGraphApiUserProperties()}`)
                .post(directoryObject));
        }
        return forkJoin(requests).pipe(map((responses) => responses));
    }
    /*
     * Properties used for $select (query param) user details on the new graph api version
     */
    getMsGraphApiUserProperties() {
        const userProperties = [
            'displayName',
            'userType',
            'givenName',
            'mail',
            'surname',
            'usageLocation',
            'userPrincipalName',
            'id',
            'city',
            'companyName',
            'country',
            'lastDirSyncTime',
            'mailNickname',
            'externalUserState'
        ];
        return `${'$select='}${userProperties.toString()}`;
    }
    /**
     * Retrieves all the user data from the passed in user ldaps.
     * This array should have a max of 10 items, Graph api doesn't allow for more conditions at one time.
     * which is the mailNickname in the graph url.
     *
     * @param ldaps user ldap list
     * @returns UserProfile list
     */
    getUsersByMailNickName(ldaps) {
        const query = `${this.getMsGraphApiUserProperties()}&$filter=(mailNickname eq '${ldaps.join("' or mailNickname eq '")}')`;
        const usersById = this.msalService.graphClient.api(`${'/users?'}${query}`).get();
        return from(usersById).pipe(map((resUsers) => {
            const responsePayLoad = resUsers.value.map((element) => this.msGraphApiHelper.mapUserProfileToOldGraphApi(element));
            return responsePayLoad;
        }));
    }
    static { this.ɵfac = function MsGraphApiService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || MsGraphApiService)(i0.ɵɵinject(i1.LoggerService), i0.ɵɵinject(i2.MSALService), i0.ɵɵinject(i3.MsGraphApiHelper)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MsGraphApiService, factory: MsGraphApiService.ɵfac, providedIn: 'root' }); }
}
//# sourceMappingURL=ms-graph-api.service.js.map